import { ChevronRight } from 'blaze-ui/icons/24'
import Styles from './ListItem.module.scss'
import { Text } from 'blaze-ui/Text/Text'
import { Pressable } from '@react-aria/interactions'
import { AlmanacFile, AlmanacFileSummary, TextChannelKey } from 'types'
import { AspectRatioKey } from 'components/NewFileModal/Types'
import { Data } from 'hooks/queries/layers/aiSuggestionNodes/useCreateAISuggestionNode'
import { ContentType } from 'components/ContentType/ContentType'
import { PricePill } from 'blaze-ui/Pricing/PricePill/PricePill'
import { useRootStore } from 'stores/RootStore'
import { Icon } from 'blaze-ui/icons/Types'
import { ReactNode } from 'react'
import { Image } from 'blaze-ui/icons'
import { NewFileDrawer } from '../../NewFileDrawer'
import TurnDocsIntoContent from 'blaze-ui/icons/24/TurnDocsIntoContext'
import { FormSchema, FormStatus } from 'components/Wizards/Wizard/Form/Types'
import { MultiStepSchema, StepSchema } from 'components/Wizards/schemas/Types'
import cx from 'classnames'
import { ListItemProps, NewDocListItemProps } from './Types'
import { useNeedsUpgradeForChannelType } from 'hooks/useChannelNeedsUpgrade'
import { getChannelInfo } from 'utils/wizards'

export const ListItem = ({ title, icon, onPress, ref, badge, className, withChevron = true }: ListItemProps) => {
  return (
    <div onPointerDown={(e) => e.stopPropagation()} className={cx(Styles.listItemContainer, className)}>
      <Pressable onPress={onPress} ref={ref}>
        <div
          className={Styles.listItem}
          role="button"
          tabIndex={0}
          aria-label={typeof title === 'string' ? title : undefined}
        >
          <div className={Styles.icon}>{icon}</div>
          <Text variant="largeList">{title}</Text>
          {badge}
          {withChevron && (
            <div className={Styles.chevron}>
              <ChevronRight size={16} color={'var(--dark-40)'} />
            </div>
          )}
        </div>
      </Pressable>
    </div>
  )
}

export const NewDocListItem = ({ title, icon }: NewDocListItemProps) => {
  const { drawersStore } = useRootStore()

  return (
    <ListItem
      title={title || 'Blank Doc'}
      icon={icon || <TurnDocsIntoContent />}
      withChevron={false}
      onPress={() =>
        drawersStore.openDrawer(NewFileDrawer, {
          onSuccess: () => drawersStore.closeDrawer(),
        })
      }
    />
  )
}

export const NewDesignListItem = ({ title, icon, initialAspectRatioKey }: NewDocListItemProps) => {
  const { drawersStore } = useRootStore()

  return (
    <ListItem
      title={title || 'Blank Design'}
      icon={icon || <Image />}
      withChevron={false}
      onPress={() =>
        drawersStore.openDrawer(NewFileDrawer, {
          onSuccess: () => drawersStore.closeDrawer(),
          initialFileType: 'design',
          initialAspectRatioKey,
        })
      }
    />
  )
}

export interface WizardListItemProps {
  channelKey: TextChannelKey | AspectRatioKey
  initialValues?: object
  onSuccess?: (data: { almanac_file: AlmanacFile | Omit<AlmanacFileSummary, 'ancestors'> } | Data) => void
  title?: string | ReactNode
  disabled?: boolean
  icon?: Icon
  schemaModifier?: <T extends FormSchema | MultiStepSchema | StepSchema>(schema: T) => T
  initialFormStatus?: FormStatus
}

export const WizardListItem = ({
  channelKey,
  initialValues,
  onSuccess,
  title,
  disabled = false,
  icon,
  schemaModifier,
  initialFormStatus,
}: WizardListItemProps) => {
  const { name, icon: Icon } = getChannelInfo(channelKey)

  const FinalIcon = icon ?? Icon

  const item = {
    title: name,
    icon: <FinalIcon size={24} />,
  }

  const { shouldForceUpgrade, nextTierPlan } = useNeedsUpgradeForChannelType()

  return (
    <ContentType
      type={channelKey}
      key={channelKey}
      initialValues={initialValues}
      onSuccess={onSuccess}
      isDisabled={disabled}
      schemaModifier={schemaModifier}
      initialFormStatus={initialFormStatus}
    >
      <ListItem
        key={item.title}
        onPress={() => {}}
        withChevron={false}
        badge={
          shouldForceUpgrade(channelKey as TextChannelKey) && nextTierPlan ? (
            <div className={Styles.upgradeBadge} data-testid="upgrade-badge-container">
              <PricePill plan={nextTierPlan.key} />
            </div>
          ) : (
            <></>
          )
        }
        title={title || item.title}
        icon={item.icon}
      />
    </ContentType>
  )
}
